import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Package, Plus, Search, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import CreateLoadDialog from '@/components/admin/CreateLoadDialog';
import { getLoadStatusClasses, getDeliveryTypeClasses } from '@/lib/statusColors';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);

const STATUS_LABELS = {
  draft: 'Draft', posted: 'Open', assigned: 'Assigned', at_pickup: 'At Pickup',
  in_transit: 'On Route', at_delivery: 'At Delivery', completed: 'Delivered', invoiced: 'Invoiced',
};

const TRANSITIONS = {
  draft: ['posted'], posted: ['assigned'], assigned: ['at_pickup'],
  at_pickup: ['in_transit'], in_transit: ['at_delivery'], at_delivery: ['completed'], completed: ['invoiced'],
};

const TAX_RATE = 0.13; // HST 13%

export default function AdminLoads() {
  const { token, user } = useAuth();
  const [loads, setLoads] = useState([]);
  const [filter, setFilter] = useState('all');
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('all');

  const fetchData = () => {
        const params = filter !== 'all' ? { status: filter } : {};
    Promise.all([api.get('/loads', { params }), api.get('/drivers')])
      .then(([lr, dr]) => { setLoads(lr.data); setDrivers(dr.data); })
      .catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [token, filter]);

  const handleStatusChange = async (loadId, newStatus) => {
    try {
      await api.put(`/loads/${loadId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleAssign = async (loadId, driverId) => {
    try {
      await api.put(`/loads/${loadId}/assign`, { driver_id: driverId });
      toast.success('Driver assigned');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign'); }
  };

  const getDriverName = (driverId) => drivers.find(d => d.user_id === driverId)?.name || null;

  // Apply filters
  let filtered = loads;
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(l =>
      l.cargo_description?.toLowerCase().includes(q) || l.load_id?.toLowerCase().includes(q) ||
      l.reference_number?.toLowerCase().includes(q) ||
      l.pickup_address?.toLowerCase().includes(q) || l.delivery_address?.toLowerCase().includes(q) ||
      getDriverName(l.driver_id)?.toLowerCase().includes(q)
    );
  }
  if (dateStart) filtered = filtered.filter(l => new Date(l.created_at) >= new Date(dateStart));
  if (dateEnd) filtered = filtered.filter(l => new Date(l.created_at) <= new Date(dateEnd + 'T23:59:59'));
  if (vehicleFilter !== 'all') filtered = filtered.filter(l => l.equipment_type_required === vehicleFilter);

  return (
    <div data-testid="admin-loads">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Load Management</h1>
          <p className="text-muted-foreground text-sm">{loads.length} loads total</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-brand-accent hover:bg-yellow-500 text-brand-accent-foreground font-bold rounded-lg text-xs tracking-wide h-9 px-4">
          <Plus className="w-4 h-4 mr-1" /> NEW LOAD
        </Button>
      </div>

      {/* Filters Row */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search loads..."
            className="w-full bg-muted border border-input text-foreground text-xs rounded-lg pl-8 pr-3 py-2" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="bg-muted border-input text-muted-foreground w-36 rounded-lg text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-muted border-input">
            <SelectItem value="all" className="text-foreground">All Status</SelectItem>
            {Object.keys(STATUS_LABELS).map(s => (
              <SelectItem key={s} value={s} className="text-foreground">{STATUS_LABELS[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
          <SelectTrigger className="bg-muted border-input text-muted-foreground w-36 rounded-lg text-xs">
            <SelectValue placeholder="Vehicle Type" />
          </SelectTrigger>
          <SelectContent className="bg-muted border-input">
            <SelectItem value="all" className="text-foreground">All Vehicles</SelectItem>
            {['truck', 'flatbed', 'reefer', 'tanker'].map(v => (
              <SelectItem key={v} value={v} className="text-foreground">{v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
          <Input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)}
            className="bg-muted border-input text-muted-foreground h-8 text-xs w-32 rounded-lg" />
          <span className="text-muted-foreground text-xs">to</span>
          <Input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)}
            className="bg-muted border-input text-muted-foreground h-8 text-xs w-32 rounded-lg" />
        </div>
      </div>

      <Card className="bg-card border-border backdrop-blur-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">LOAD ID</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">REF #</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">ASSIGNED</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">DEL. TYPE</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">VEHICLE</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">CUSTOMER</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">PICKUP</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">DELIVERY</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">STATUS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">WEIGHT</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">PRICE</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? Array(5).fill(0).map((_, i) => (
                <TableRow key={i} className="border-border"><TableCell colSpan={11}><div className="h-8 bg-muted rounded animate-pulse" /></TableCell></TableRow>
              )) : filtered.map(load => {
                const drvName = getDriverName(load.driver_id);
                const subtotal = load.rate || 0;
                const tax = Math.round(subtotal * TAX_RATE);
                const total = subtotal + tax;
                return (
                  <TableRow key={load.load_id} className="border-border hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/admin/loads/${load.load_id}`)}>
                    <TableCell className="font-mono text-xs text-muted-foreground">{load.load_id?.slice(-8)}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{load.reference_number || '-'}</TableCell>
                    <TableCell className="text-xs">
                      {drvName ? <span className="text-emerald-400">{drvName}</span> : <span className="text-amber-400">Open</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] rounded-sm ${getDeliveryTypeClasses(load.delivery_type || 'regular')}`}>{(load.delivery_type || 'regular').toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{load.equipment_type_required || 'Truck'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{load.shipper?.name || load.client_id?.slice(-6) || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[100px] truncate">{load.pickup_name || load.pickup_address?.split(',')[0]}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[100px] truncate">{load.delivery_name || load.delivery_address?.split(',')[0]}</TableCell>
                    <TableCell><Badge className={`text-[10px] rounded-sm ${getLoadStatusClasses(load.status)}`}>{STATUS_LABELS[load.status] || load.status}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">{load.weight_lbs?.toLocaleString()} lbs</TableCell>
                    <TableCell className="text-right">
                      <div className="text-[10px] leading-tight">
                        <div className="text-muted-foreground">${subtotal.toLocaleString()}</div>
                        <div className="text-muted-foreground">+${tax} HST</div>
                        <div className="text-brand-accent font-bold">${total.toLocaleString()}</div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!loading && filtered.length === 0 && (
            <p className="text-muted-foreground text-sm text-center py-8">No loads match your filters</p>
          )}
        </CardContent>
      </Card>

      {/* Create Load Dialog */}
      <CreateLoadDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={fetchData} />
    </div>
  );
}
